import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Play, 
  Square, 
  Clock, 
  User, 
  DollarSign, 
  ShoppingCart
} from 'lucide-react';
import { formatCurrency } from '../../utils/format';
import { useSettingsStore } from '../../store/settingsStore';
import { useAuthStore } from '../../store/authStore';
import { Modal } from '../../components/common/Modal';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Button } from '../../components/common/Button';
import { useToast } from '../../components/common/Toast';
import { shiftService, type CurrentShiftResponse } from '../../services/shiftService';
import { useLanguage } from '../../features/language/useLanguage';

export const ShiftReport: React.FC = () => {
  const { t } = useLanguage();
  const { currencySymbol } = useSettingsStore();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { data: currentData, isLoading: loadingCurrent, error: currentError } = useQuery<CurrentShiftResponse>({
    queryKey: ['shift', 'current'],
    queryFn: shiftService.getCurrent,
    retry: 1,
  });

  const { data: historyData = [], isLoading: loadingHistory, error: historyError } = useQuery({
    queryKey: ['shift', 'history'],
    queryFn: () => shiftService.getHistory({ limit: 50 }),
    retry: 1,
  });

  const activeShift = currentData?.shift || null;
  const summary = currentData?.summary || null;
  const transactions = currentData?.transactions || [];
  const stockMovements = currentData?.stock_movements || [];

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isConfirmClose, setIsConfirmClose] = useState(false);

  const openerName = useMemo(() => {
    if (!activeShift) return '';
    return activeShift.opened_by_name || activeShift.opened_by;
  }, [activeShift]);

  const canClose = useMemo(() => {
    if (!activeShift || !user) return false;
    return activeShift.opened_by === user.id;
  }, [activeShift, user]);

  const closeMutation = useMutation({
    mutationFn: shiftService.close,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['shift', 'current'] });
      await queryClient.invalidateQueries({ queryKey: ['shift', 'history'] });
      setIsConfirmClose(false);
      showToast(t('reports.shift.toast.closedSuccess'), 'success');
    },
    onError: (err: unknown) => {
      const msg = (() => {
        if (typeof err === 'string') return err;
        if (err && typeof err === 'object') {
          const e = err as Record<string, unknown>;
          const response = e['response'] as Record<string, unknown> | undefined;
          const data = response?.['data'] as Record<string, unknown> | undefined;
          const errorText = data?.['error'];
          if (typeof errorText === 'string') return errorText;
        }
        return (err as Error).message || t('reports.shift.toast.closeFailed');
      })();
      showToast(msg, 'error');
    }
  });

  const handleCloseShift = () => {
    closeMutation.mutate();
  };

  return (
    <div className="space-y-6" data-test-id="shift-report-container">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('reports.shift.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('reports.shift.subtitle')}
          </p>
        </div>
      </div>

      {/* Current Shift Status */}
      <div className="bg-white shadow sm:rounded-lg overflow-hidden" data-test-id="current-shift-status">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">{t('reports.shift.currentStatus')}</h3>
          
          {loadingCurrent ? (
            <LoadingSpinner text={t('reports.shift.loadingCurrent')} />
          ) : currentError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {t('reports.shift.errorCurrent')}
            </div>
          ) : activeShift ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-full mr-3">
                    <Clock className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">{t('reports.shift.openTitle')}</h4>
                    <p className="text-sm text-gray-500">{t('reports.shift.startedAt', { value: new Date(activeShift.start_time).toLocaleString() })}</p>
                  </div>
                </div>
                <Button
                  onClick={() => setIsConfirmClose(true)}
                  variant="danger"
                  size="md"
                  leftIcon={<Square className="h-4 w-4" />}
                  disabled={!canClose}
                  title={canClose ? undefined : t('reports.shift.openedByTitle', { name: openerName })}
                  data-test-id="close-shift-button"
                >
                  {t('reports.shift.closeShift')}
                </Button>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-md shadow-sm border border-green-100">
                  <div className="text-sm font-medium text-gray-500 mb-1">{t('reports.shift.openedBy')}</div>
                  <div className="flex items-center">
                    <User className="h-4 w-4 mr-2 text-gray-400" />
                    <span className="font-medium">{openerName}</span>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-md shadow-sm border border-green-100">
                  <div className="text-sm font-medium text-gray-500 mb-1">{t('reports.shift.currentSales')}</div>
                  <div className="flex items-center">
                    <DollarSign className="h-4 w-4 mr-2 text-gray-400" />
                    <span className="font-medium text-green-600">{formatCurrency(summary?.total_sales || 0, currencySymbol)}</span>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-md shadow-sm border border-green-100">
                  <div className="text-sm font-medium text-gray-500 mb-1">{t('reports.shift.cost')}</div>
                  <div className="flex items-center">
                    <DollarSign className="h-4 w-4 mr-2 text-gray-400" />
                    <span className="font-medium text-red-600">{formatCurrency(summary?.total_purchases || 0, currencySymbol)}</span>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-md shadow-sm border border-green-100">
                  <div className="text-sm font-medium text-gray-500 mb-1">{t('reports.shift.profit')}</div>
                  <div className="flex items-center">
                    <DollarSign className="h-4 w-4 mr-2 text-gray-400" />
                    <span className="font-medium text-blue-600">{formatCurrency(summary?.net_cash_flow || 0, currencySymbol)}</span>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-md shadow-sm border border-green-100">
                  <div className="text-sm font-medium text-gray-500 mb-1">{t('reports.shift.transactions')}</div>
                  <div className="flex items-center">
                    <ShoppingCart className="h-4 w-4 mr-2 text-gray-400" />
                    <span className="font-medium">{t('reports.shift.ordersCount', { count: summary?.transactions_count || 0 })}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                <Clock className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">{t('reports.shift.noActiveTitle')}</h3>
              <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
                {t('reports.shift.noActiveDescription')}
              </p>
              <div className="mt-6">
                <Button
                  onClick={() => setIsConfirmOpen(true)}
                  variant="primary"
                  size="md"
                  leftIcon={<Play className="h-4 w-4" />}
                  data-test-id="start-shift-button"
                >
                  {t('reports.shift.startShift')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Shift History */}
      <div className="bg-white shadow sm:rounded-lg overflow-hidden" data-test-id="shift-history-section">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">{t('reports.shift.historyTitle')}</h3>
        </div>
        <div className="overflow-x-auto">
          {loadingHistory ? (
            <div className="p-6"><LoadingSpinner text={t('reports.shift.loadingHistory')} /></div>
          ) : historyError ? (
            <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-red-700">{t('reports.shift.errorHistory')}</div>
          ) : (
          <table className="min-w-full divide-y divide-gray-200" data-test-id="shift-history-table">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.shift.period')}</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.shift.openedBy')}</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.shift.closedBy')}</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.shift.totalSales')}</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.shift.transactions')}</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.shift.status')}</th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">{t('reports.shift.view')}</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {historyData.filter(s => s.status === 'closed').map((shift) => (
                <tr key={shift.id} className="hover:bg-gray-50" data-test-id={`shift-history-row-${shift.id}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex flex-col">
                      <span className="font-medium">{new Date(shift.start_time).toLocaleDateString()}</span>
                      <span className="text-xs text-gray-500">{new Date(shift.start_time).toLocaleTimeString()} - {shift.end_time ? new Date(shift.end_time).toLocaleTimeString() : '-'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{shift.opened_by_name || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{shift.closed_by_name || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                    {formatCurrency(shift.total_sales || 0, currencySymbol)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">{shift.transactions_count || 0}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {t('reports.shift.closed')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-primary-600 hover:text-primary-900" data-test-id={`view-shift-details-${shift.id}`}>
                      {t('reports.shift.viewDetails')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
      </div>

      {/* Modals */}
      <Modal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title={t('reports.shift.startNewTitle')}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            {t('reports.shift.startNewDescription')}
          </p>
          <div className="flex justify-end gap-3 mt-6">
            <Button
              onClick={() => setIsConfirmOpen(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={() => setIsConfirmOpen(false)}
              variant="primary"
            >
              {t('reports.shift.startShift')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isConfirmClose}
        onClose={() => setIsConfirmClose(false)}
        title={t('reports.shift.summaryTitle')}
        maxWidth="4xl"
      >
        <div className="space-y-6">
          {/* Summary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border rounded-lg p-4">
              <div className="text-sm text-gray-500">{t('reports.shift.totalSales')}</div>
              <div className="text-lg font-semibold text-green-700">{formatCurrency(summary?.total_sales || 0, currencySymbol)}</div>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <div className="text-sm text-gray-500">{t('reports.shift.totalPurchases')}</div>
              <div className="text-lg font-semibold text-red-700">{formatCurrency(summary?.total_purchases || 0, currencySymbol)}</div>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <div className="text-sm text-gray-500">{t('reports.shift.netCashFlow')}</div>
              <div className="text-lg font-semibold text-blue-700">{formatCurrency(summary?.net_cash_flow || 0, currencySymbol)}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border rounded-lg p-4">
              <div className="text-sm text-gray-500">{t('reports.shift.totalTransactions')}</div>
              <div className="text-lg font-semibold">{summary?.transactions_count || 0}</div>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <div className="text-sm text-gray-500">{t('reports.shift.itemsIn')}</div>
              <div className="text-lg font-semibold text-green-700">{summary?.inventory_in_count || 0}</div>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <div className="text-sm text-gray-500">{t('reports.shift.itemsOut')}</div>
              <div className="text-lg font-semibold text-red-700">{summary?.inventory_out_count || 0}</div>
            </div>
          </div>

          {/* Detailed Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">{t('reports.shift.financialTransactions')}</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('reports.shift.id')}</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('reports.shift.type')}</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('reports.shift.amount')}</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('reports.shift.time')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {transactions.length === 0 ? (
                      <tr><td colSpan={4} className="px-4 py-3 text-center text-gray-500">{t('reports.shift.noTransactions')}</td></tr>
                    ) : (
                      transactions.map(tx => (
                        <tr key={tx.id}>
                          <td className="px-4 py-2 text-sm text-gray-900">{tx.id}</td>
                          <td className="px-4 py-2 text-sm capitalize">{tx.type}</td>
                          <td className="px-4 py-2 text-sm text-right">{formatCurrency(tx.amount, currencySymbol)}</td>
                          <td className="px-4 py-2 text-sm">{new Date(tx.time).toLocaleString()}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">{t('reports.shift.inventoryMovements')}</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('reports.shift.item')}</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('reports.shift.type')}</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('reports.shift.quantity')}</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('reports.shift.reason')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stockMovements.length === 0 ? (
                      <tr><td colSpan={4} className="px-4 py-3 text-center text-gray-500">{t('reports.shift.noMovements')}</td></tr>
                    ) : (
                      stockMovements.map((mv, idx) => (
                        <tr key={`${mv.item_name}-${idx}`}>
                          <td className="px-4 py-2 text-sm text-gray-900">{mv.item_name}</td>
                          <td className="px-4 py-2 text-sm capitalize">{mv.type}</td>
                          <td className="px-4 py-2 text-sm text-right">{mv.quantity}</td>
                          <td className="px-4 py-2 text-sm">{mv.reason || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  {t('reports.shift.reviewBeforeClose')}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-2">
            <Button
              onClick={() => setIsConfirmClose(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCloseShift}
              variant="danger"
              isLoading={closeMutation.isPending}
              disabled={!canClose}
              title={canClose ? undefined : t('reports.shift.onlyOpenerCanClose', { name: openerName })}
              data-test-id="confirm-close-shift-button"
            >
              {t('reports.shift.confirmClose')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
